import { useState } from "react";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import useAdd from "../../../api/useAdd";
import useUpdate from "../../../api/useUpdate";
import useDelete from "../../../api/useDelete";
import SeminarFormDialog from "./SeminarFormDialog";
import { Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TablePagination, CircularProgress, Alert, Button, IconButton } from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";

export default function SeminarList() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSeminar, setEditingSeminar] = useState(null);

  const { data: usersData } = useFetchObjects(["users-sem"], "users/?page_size=500", token);
  const { data: academicsData } = useFetchObjects(["academics-sem"], "academics/?page_size=500&status=1", token);
  const { data, isLoading, isError, error } = useFetchObjects(
    ["seminars", page, rowsPerPage],
    `seminars/?page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const { handleAdd } = useAdd("seminars", token, t("academic.seminarAdded", "Seminar added."));
  const { handleUpdate } = useUpdate("seminars", token);
  const { handleDelete, ConfirmDialog } = useDelete("seminars", token);

  const rows = Array.isArray(data) ? data : (data?.results || []);
  const totalCount = Array.isArray(data) ? data.length : (data?.count || 0);
  const users = usersData?.results || usersData || [];
  const academics = academicsData?.results || academicsData || [];

  const handleOpenAdd = () => { setEditingSeminar(null); setDialogOpen(true); };
  const handleOpenEdit = (row) => { setEditingSeminar(row); setDialogOpen(true); };
  const handleCloseDialog = () => { setDialogOpen(false); setEditingSeminar(null); };
  const handleSave = (payload) => { if (editingSeminar) handleUpdate(editingSeminar.id, payload); else handleAdd(payload); handleCloseDialog(); };

  if (isError) return <Alert severity="error" sx={{ m: 2 }}>{error?.message || "Failed to load seminars"}</Alert>;
  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ConfirmDialog />
      <Paper sx={{ p: 3, width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>{t("academic.seminars", "Seminars")}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>{t("academic.addSeminar", "Add Seminar")}</Button>
        </Box>
        {isLoading ? <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box> : (
          <>
            <TableContainer sx={{ width: "100%", overflow: "auto" }}>
              <Table size="small" sx={{ minWidth: 400 }}>
                <TableHead><TableRow sx={{ bgcolor: "grey.200" }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>{t("academic.seminarTitle", "Title")}</strong></TableCell>
                  <TableCell><strong>{t("academic.date", "Date")}</strong></TableCell>
                  <TableCell><strong>{t("academic.location", "Location")}</strong></TableCell>
                  <TableCell><strong>{t("academic.organizer", "Organizer")}</strong></TableCell>
                  <TableCell align="right"><strong>{t("common.actions", "Actions")}</strong></TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {rows.length === 0 ? <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>{t("common.noRecords", "No records found.")}</TableCell></TableRow> : rows.map((row, idx) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>{row.seminar_date || "—"}</TableCell>
                      <TableCell>{row.location || "—"}</TableCell>
                      <TableCell>{row.organizer_display || "—"}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenEdit(row)} title={t("common.edit", "Edit")}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => handleDelete(row.id)} color="error" title={t("common.delete", "Delete")}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={totalCount} page={page - 1} onPageChange={(e, p) => setPage(p + 1)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(1); }} rowsPerPageOptions={[10, 25, 50]} />
          </>
        )}
      </Paper>
      <SeminarFormDialog open={dialogOpen} onClose={handleCloseDialog} seminar={editingSeminar} users={users} academics={academics} onSave={handleSave} saving={false} />
    </Box>
  );
}
